import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Layout, { Section } from "@/components/layout/Layout";
import { Reveal } from "@/components/common/Reveal";
import { HeroConstellation, Node, Path, Flow, Field, Spark } from "@/components/primitives/Primitives";
import VentaraMark from "@/components/common/VentaraMark";
import { EASE } from "@/lib/motion";
import { ArrowUpRight } from "lucide-react";

const capabilities = [
    { k: "01", t: "AI Transformation", d: "A quiet re-architecture of how work happens." },
    { k: "02", t: "Agentic Systems", d: "Autonomy with judgment. Legible by design." },
    { k: "03", t: "Voice AI", d: "Interfaces you speak to — in your own language." },
    { k: "04", t: "Localized LLMs", d: "Models tuned to the culture they serve." },
    { k: "05", t: "Workflow Automation", d: "Fewer clicks. Better outcomes." },
    { k: "06", t: "Knowledge Systems", d: "The right sentence at the right moment." },
];

const products = [
    { name: "Sonare", role: "Voice", note: "A voice canvas for enterprise conversations." },
    { name: "Atlas", role: "Knowledge", note: "Institutional memory that answers back." },
    { name: "Foreground", role: "Agents", note: "Agentic workflows with a human in the frame." },
    { name: "Lumen", role: "Studio", note: "A creative surface for media intelligence." },
];

const industries = ["Finance", "Healthcare", "Media", "Retail", "Public Sector", "Manufacturing", "Education", "Telecom"];

export default function Home() {
    const { t } = useTranslation();
    return (
        <Layout title="Ventara — Make Intelligence Feel Natural" description="Ventara builds AI-native platforms, intelligent products, and human-centered experiences.">
            {/* HERO — Midnight navy canvas with brand mark & gradient sunrise */}
            <section
                className="relative overflow-hidden -mt-16 pt-32 sm:pt-40 pb-24 sm:pb-32"
                data-testid="home-hero"
                style={{
                    background: "hsl(var(--midnight-navy))",
                    color: "hsl(var(--cloud-white))",
                }}
            >
                {/* Aurora backdrop */}
                <div
                    className="pointer-events-none absolute inset-0 opacity-70"
                    style={{
                        background:
                            "radial-gradient(1000px 500px at 82% 18%, rgba(255,138,0,0.20), transparent 55%), radial-gradient(900px 500px at 18% 88%, rgba(138,43,226,0.28), transparent 55%), radial-gradient(700px 400px at 55% 50%, rgba(255,77,166,0.10), transparent 60%)",
                    }}
                />
                {/* Subtle constellation lattice */}
                <div
                    className="pointer-events-none absolute inset-0 opacity-40"
                >
                    <HeroConstellation className="w-full h-full" variant="brand" />
                </div>

                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 relative">
                    <div className="grid lg:grid-cols-12 gap-8 items-center">
                        <div className="lg:col-span-8">
                            <motion.p
                                className="label"
                                style={{ color: "hsl(var(--cloud-white) / 0.55)" }}
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, ease: EASE }}
                                data-testid="hero-eyebrow"
                            >
                                {t("home.eyebrow")}
                            </motion.p>

                            <motion.h1
                                className="font-serif font-light text-5xl sm:text-7xl lg:text-[8rem] leading-[0.94] tracking-tight mt-10 max-w-5xl"
                                initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                                data-testid="hero-title"
                            >
                                {t("home.heroA")}<br />
                                <span className="italic brand-gradient-text">{t("home.heroB")}</span>
                            </motion.h1>

                            <motion.div
                                className="mt-12 max-w-2xl"
                                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.25 }}
                            >
                                <p className="prose-body text-lg sm:text-xl" style={{ color: "hsl(var(--cloud-white) / 0.72)" }}>
                                    {t("home.lead")}
                                </p>
                                <div className="mt-10 flex flex-wrap items-center gap-4">
                                    <Link to="/products" data-testid="hero-primary-cta"
                                          className="brand-gradient inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm text-white shadow-[0_10px_40px_-10px_rgba(255,77,166,0.5)] hover:brightness-110 transition">
                                        {t("home.primary")} <ArrowUpRight className="h-4 w-4" />
                                    </Link>
                                    <Link to="/about" data-testid="hero-secondary-cta"
                                          className="text-sm border-b border-transparent hover:border-white/40 transition-colors"
                                          style={{ color: "hsl(var(--cloud-white) / 0.7)" }}>
                                        {t("home.secondary")} →
                                    </Link>
                                </div>
                            </motion.div>
                        </div>

                        <motion.div
                            className="lg:col-span-4 hidden lg:flex justify-end"
                            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, ease: EASE, delay: 0.4 }}
                        >
                            <VentaraMark size={320} animate />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* SECTION 1 — WHO WE ARE */}
            <Section className="py-32 border-t border-border" data-testid="home-who">
                <div className="grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-4">
                        <p className="label">{t("home.sections.s1.label")}</p>
                    </div>
                    <div className="lg:col-span-8">
                        <Reveal>
                            <p className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight">
                                {t("home.sections.s1.body")}
                            </p>
                        </Reveal>
                        <Reveal delay={0.2} className="mt-12 grid sm:grid-cols-3 gap-8">
                            {[
                                ["24", "Countries deployed"],
                                ["9", "Languages supported"],
                                ["120M+", "Interactions handled monthly"],
                            ].map(([n, l]) => (
                                <div key={l}>
                                    <div className="font-serif text-4xl">{n}</div>
                                    <div className="label mt-2">{l}</div>
                                </div>
                            ))}
                        </Reveal>
                    </div>
                </div>
            </Section>

            {/* SECTION 2 — PHILOSOPHY */}
            <Section className="py-32 border-t border-border" data-testid="home-philosophy">
                <div className="grid lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5 text-foreground">
                        <Path className="w-full h-32" variant="brand" />
                    </div>
                    <div className="lg:col-span-7">
                        <p className="label">{t("home.sections.s2.label")}</p>
                        <Reveal>
                            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-6">
                                {t("home.sections.s2.title")}
                            </h2>
                        </Reveal>
                    </div>
                </div>
            </Section>

            {/* SECTION 3 — CAPABILITIES */}
            <Section className="py-32 border-t border-border" data-testid="home-capabilities">
                <div className="flex items-end justify-between mb-16">
                    <div>
                        <p className="label">{t("home.sections.s3.label")}</p>
                        <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 tracking-tight">{t("home.sections.s3.title")}</h2>
                    </div>
                    <Link to="/solutions" className="hidden sm:inline text-sm text-muted-foreground hover:text-foreground">All solutions →</Link>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                    {capabilities.map((c) => (
                        <div key={c.t} className="bg-background p-10 group" data-testid={`capability-${c.k}`}>
                            <div className="label text-foreground/40">{c.k}</div>
                            <h3 className="font-serif text-2xl mt-6 tracking-tight">{c.t}</h3>
                            <p className="prose-body text-sm mt-4">{c.d}</p>
                        </div>
                    ))}
                </div>
            </Section>

            {/* SECTION 4 — PRODUCTS */}
            <Section className="py-32 border-t border-border" data-testid="home-products">
                <div className="grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-4">
                        <p className="label">{t("home.sections.s4.label")}</p>
                        <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 tracking-tight">{t("home.sections.s4.title")}</h2>
                        <Link to="/products" className="mt-8 inline-block text-sm text-muted-foreground hover:text-foreground border-b border-transparent hover:border-foreground/40 transition-colors">
                            See all products →
                        </Link>
                    </div>
                    <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
                        {products.map((p, i) => (
                            <Reveal key={p.name} delay={i * 0.06}>
                                <Link to="/products" className="block rounded-3xl border border-border p-10 hover:border-foreground/30 transition-colors h-full">
                                    <div className="flex items-start justify-between">
                                        <p className="label">{p.role}</p>
                                        <ArrowUpRight className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                    <h3 className="font-serif text-3xl mt-10 tracking-tight">{p.name}</h3>
                                    <p className="prose-body text-sm mt-4">{p.note}</p>
                                    <div className="mt-10 h-24 text-foreground/70 opacity-60">
                                        {i % 4 === 0 && <Node className="h-full w-auto" size={90} />}
                                        {i % 4 === 1 && <Flow className="w-full h-full" />}
                                        {i % 4 === 2 && <Path className="w-full h-full" />}
                                        {i % 4 === 3 && <Spark className="h-full w-auto" />}
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </Section>

            {/* SECTION 5 — INDUSTRIES */}
            <Section className="py-32 border-t border-border" data-testid="home-industries">
                <p className="label">{t("home.sections.s5.label")}</p>
                <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl mt-6 tracking-tight max-w-4xl">{t("home.sections.s5.title")}</h2>
                <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-px bg-border">
                    {industries.map((i) => (
                        <div key={i} className="bg-background py-10 px-6" data-testid={`industry-${i.toLowerCase().replace(/\s+/g,'-')}`}>
                            <div className="font-serif text-2xl">{i}</div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* SECTION 6 — TECHNOLOGY */}
            <Section className="py-32 border-t border-border" data-testid="home-technology">
                <div className="grid lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-6">
                        <p className="label">{t("home.sections.s6.label")}</p>
                        <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 tracking-tight">The quiet stack.</h2>
                        <p className="prose-body mt-8 max-w-lg">
                            LLMs. Voice AI. Speech. AI Agents. Knowledge Systems. Vector Search. Automation. Inference. Multimodal AI.
                            We build the layer that disappears — which is another way of saying, the layer you can trust.
                        </p>
                        <Link to="/technology" className="mt-8 inline-block text-sm text-muted-foreground hover:text-foreground border-b border-transparent hover:border-foreground/40 transition-colors">
                            Explore the stack →
                        </Link>
                    </div>
                    <div className="lg:col-span-6 text-foreground">
                        <Flow className="w-full h-64" variant="brand" />
                    </div>
                </div>
            </Section>

            {/* SECTION 7 — VDL */}
            <Section className="py-32 border-t border-border" data-testid="home-vdl">
                <div className="grid lg:grid-cols-12 gap-16 items-center">
                    <div className="lg:col-span-5 text-foreground/70">
                        <div className="grid grid-cols-3 gap-6">
                            <Node size={80} variant="brand" />
                            <div className="flex items-center"><Path className="w-full h-16" variant="brand" /></div>
                            <Spark variant="brand" />
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <p className="label">{t("home.sections.s7.label")}</p>
                        <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 tracking-tight">{t("home.sections.s7.title")}</h2>
                        <p className="prose-body mt-8 max-w-xl">
                            A constitution, a manifesto, a system. Five primitives — Node, Path, Flow, Field, Spark — that describe how intelligence should look, move, and feel.
                        </p>
                        <Link to="/vdl" className="mt-8 inline-block text-sm text-muted-foreground hover:text-foreground border-b border-transparent hover:border-foreground/40 transition-colors">
                            Read the VDL →
                        </Link>
                    </div>
                </div>
            </Section>

            {/* SECTION 8 — RESEARCH */}
            <Section className="py-32 border-t border-border" data-testid="home-research">
                <div className="grid lg:grid-cols-12 gap-16">
                    <div className="lg:col-span-4">
                        <p className="label">{t("home.sections.s8.label")}</p>
                        <h2 className="font-serif font-light text-4xl sm:text-5xl mt-6 tracking-tight">{t("home.sections.s8.title")}</h2>
                    </div>
                    <div className="lg:col-span-8 space-y-6">
                        {[
                            ["2026", "On legible autonomy in agentic systems"],
                            ["2025", "Localized voice models for low-resource languages"],
                            ["2024", "Editorial interfaces for enterprise AI"],
                        ].map(([y, t]) => (
                            <div key={t} className="flex items-start justify-between gap-8 pb-6 border-b border-border" data-testid={`research-${y}`}>
                                <div className="flex-1">
                                    <div className="label">{y}</div>
                                    <div className="mt-2 font-serif text-2xl tracking-tight">{t}</div>
                                </div>
                                <ArrowUpRight className="h-5 w-5 text-muted-foreground" />
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* SECTION 9 — CTA */}
            <Section className="py-32 border-t border-border" data-testid="home-cta">
                <div className="max-w-4xl">
                    <p className="label">{t("home.sections.s9.label")}</p>
                    <h2 className="font-serif font-light text-5xl sm:text-7xl lg:text-8xl mt-8 tracking-tight leading-[0.95]">
                        {t("home.sections.s9.title")}
                    </h2>
                    <Link to="/contact" data-testid="home-cta-btn"
                          className="brand-gradient mt-14 inline-flex items-center gap-3 rounded-full px-8 py-4 text-sm text-white shadow-[0_10px_40px_-10px_rgba(255,77,166,0.35)] hover:brightness-110 transition">
                        Start a conversation <ArrowUpRight className="h-4 w-4" />
                    </Link>
                </div>
            </Section>
        </Layout>
    );
}
